import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { Badge } from 'src/components/Badge';
import { Skeleton } from 'src/components/Skeleton';
import { Visit } from 'src/models/visit/_types';

import { Avatar } from '../../components/Avatar';
import { Box } from '../../components/Box';
import { Card, CardActionArea, CardContent } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { Stack } from '../../components/Stack';
import { Typography } from '../../components/Typography';
import { useSession } from '../../contexts/SessionContext';
import {
  isAppointmentFuture,
  isSpotAvailable,
} from '../../models/appointment/utils';
import { getUserDisplayName, isTrainer } from '../../models/user/utils';
import {
  isCanceled,
  isCheckedIn,
  isNoShowed,
} from '../../models/visit/utils';
import { formatListDateAndTime } from '../../utils/dateFormatter';

import { AppointmentImage } from './AppointmentImage';

import type { Appointment } from 'src/models/appointment/_types';
import type { AccountManager } from 'src/models/user/_types';

interface AppointmentCardProps {
  appointment: Appointment;
  onClick?: () => void;
  showStaff?: boolean;
  dependents?: AccountManager[];
}

export function AppointmentCard({
  appointment,
  onClick,
  showStaff,
  dependents,
}: AppointmentCardProps) {
  const { user } = useSession();

  return (
    <Card>
      <CardActionArea onClick={onClick}>
        <CardContent
          sx={{
            p: 2,
            pb: (theme) => {
              return `${theme.spacing(2)} !important`;
            },
          }}
        >
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <AppointmentImage
                appointment={appointment}
                height={66}
                width={80}
              />
              <Stack spacing={1}>
                <Typography variant="h6">
                  {formatListDateAndTime(appointment.startTime)}
                </Typography>
                <Typography>{appointment.serviceName}</Typography>
              </Stack>
            </Stack>
            {isTrainer(user) &&
            (appointment.visits.length || appointment.entries.length) ? (
              <CardDetailsForTrainer
                appointment={appointment}
                showStaff={showStaff}
              />
            ) : (
              <CardDetailsForUser
                appointment={appointment}
                dependents={dependents}
              />
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export function AppointmentCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <Skeleton variant="rectangular" height={64} width={80} />
          </Box>
          <Stack spacing={1}>
            <Typography variant="h6">
              <Skeleton width={150} />
            </Typography>
            <Typography>
              <Skeleton width={170} />
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

const CardDetailsForUser = ({
  appointment,
  dependents,
}: AppointmentCardProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.card',
  });
  const { user } = useSession();
  const visit =
    appointment.visits.find((v) => v.userId === user.id) ||
    appointment.visits.find(
      (v) =>
        v.userId && dependents?.map((d) => d.dependentId).includes(v.userId)
    );
  const waitlistEntry =
    appointment.entries.find((e) => e.userId === user.id) ||
    appointment.entries.find((e) =>
      dependents?.map((d) => d.dependentId).includes(e.userId)
    );

  const waitlistPosition =
    appointment.entries.findIndex((e) => e == waitlistEntry) + 1;

  const isDependent =
    (visit &&
      visit.userId != user.id &&
      dependents?.find((d) => d.dependentId == visit.userId)) ||
    (waitlistEntry && waitlistEntry.userId != user.id);

  const showBottom =
    isDependent ||
    (visit &&
      (isDependent ||
        isCheckedIn(visit) ||
        isCanceled(visit) ||
        isNoShowed(visit)));

  return (
    <>
      {waitlistEntry && (
        <Stack direction="row" justifyContent="space-between">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            component={Typography}
            color="text.secondary"
          >
            <Typography>
              {t('waitlistPosition', { position: waitlistPosition })}
            </Typography>
          </Stack>
          {isSpotAvailable(appointment) && isAppointmentFuture(appointment) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Badge variant="dot" color="primary" />
              <Typography variant="body2" color="primary">
                {t('openSpot')}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {showBottom && (
        <Stack direction="row" alignItems="center" spacing={2}>
          {isDependent && visit && (
            <Avatar size="small" src={visit?.user?.profileImage} />
          )}
          {visit &&
            (isDependent ||
              isCheckedIn(visit) ||
              isCanceled(visit) ||
              isNoShowed(visit)) && (
              <CardDetailsLabel
                status={
                  isCheckedIn(visit)
                    ? 'checkedIn'
                    : isCanceled(visit)
                    ? 'canceled'
                    : isNoShowed(visit)
                    ? 'noshow'
                    : null
                }
                label={
                  isDependent
                    ? getUserDisplayName((visit || waitlistEntry).user)
                    : isCheckedIn(visit)
                    ? t('checkedIn')
                    : isCanceled(visit)
                    ? t('canceled')
                    : isNoShowed(visit)
                    ? t('noShow')
                    : undefined
                }
              />
            )}
          {waitlistEntry && isDependent && (
            <>
              <Avatar size="small" src={waitlistEntry?.user?.profileImage} />
              <CardDetailsLabel
                label={getUserDisplayName(waitlistEntry.user)}
              />
            </>
          )}
        </Stack>
      )}
    </>
  );
};

const CardDetailsForTrainer = ({
  appointment,
  showStaff,
}: AppointmentCardProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.card',
  });
  const defaultVisit = appointment.visits[0];
  const checkedInVisits = appointment.visits.filter((visit) =>
    isCheckedIn(visit)
  );
  const cancelledVisits = appointment.visits.filter((visit) =>
    isCanceled(visit)
  );

  let status: CardDetailsLabelProps['status'] = null;
  if (cancelledVisits.length > 0) status = 'canceled';
  if (checkedInVisits.length > 0) status = 'checkedIn';

  const totalCount = appointment.visits.filter((v: Visit) =>
    ['reserved', 'registered', 'completed'].includes(v.status)
  ).length;

  return (
    <>
      {showStaff && (
        <Typography color="text.secondary">
          {' '}
          {t('staff')} {appointment.trainerNames.join(', ')}
        </Typography>
      )}
      {appointment.isGroupClass && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar size="small" initials={totalCount.toString()} />
          <CardDetailsLabel
            status={status}
            label={`${checkedInVisits.length} ${t('checkedIn')}`}
          />
        </Stack>
      )}
      {defaultVisit && !appointment.isGroupClass && (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar size="small" src={defaultVisit.user?.profileImage} />
          <CardDetailsLabel
            status={status}
            label={getUserDisplayName(defaultVisit.user)}
          />
        </Stack>
      )}
    </>
  );
};

interface CardDetailsLabelProps {
  status?: 'checkedIn' | 'canceled' | 'noshow' | null;
  label?: string;
}
const CardDetailsLabel: React.FC<CardDetailsLabelProps> = ({
  status = null,
  label,
}) => {
  // Translation hook used for namespacing consistency
  useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.card',
  });

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {status == 'checkedIn' && (
        <Icon
          color="success"
          icon={icon({ name: 'check-circle', style: 'solid' })}
        />
      )}
      {status == 'canceled' && (
        <Icon
          color="error"
          icon={icon({ name: 'times-circle', style: 'solid' })}
        />
      )}
      {status == 'noshow' && (
        <Icon
          color="error"
          icon={icon({ name: 'question-circle', style: 'solid' })}
        />
      )}
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  );
};
